import {
  Button,
  ButtonLevel,
  ButtonSize,
  Caption,
  CollapsibleDrawer,
  type CollapsibleDrawerProps,
  type CollapsibleDrawerTriggerProps,
  type Key,
  Tab,
  TabList,
  TabPanel,
  type TabPanelProps,
  TabSize,
  TabVariant,
  Tabs,
} from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';

interface ProductAssetsDrawerV2Props extends Omit<CollapsibleDrawerProps, 'title' | 'side'> {
  /**
   * The class name of the collapsible drawer content.
   */
  contentClassName?: string;
  /**
   * Custom header content to replace the default header.
   * If provided, the default title and close button will not be rendered.
   */
  headerContent?: React.ReactNode;
}

interface ProductAssetsDrawerV2ContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The key of the selected top-level tab (hero or carousel).
   */
  selectedKey?: Key | null;
  /**
   * The key of the default selected top-level tab.
   */
  defaultSelectedKey?: Key;
  /**
   * Handler that is called when the top-level selection changes.
   */
  onSelectionChange?: (key: Key | null) => void;
  /**
   * The key of the selected carousel sub-tab (imported or spark).
   */
  carouselSubTab?: 'imported' | 'spark' | null;
  /**
   * The key of the default selected carousel sub-tab.
   */
  defaultCarouselSubTab?: 'imported' | 'spark';
  /**
   * Handler that is called when the carousel sub-tab selection changes.
   */
  onCarouselSubTabChange?: (key: 'imported' | 'spark') => void;
  /**
   * The key of the selected hero sub-tab (imported or spark).
   * Only used when the hero panel renders nested sub-tabs.
   */
  heroSubTab?: 'imported' | 'spark' | null;
  /**
   * The key of the default selected hero sub-tab.
   */
  defaultHeroSubTab?: 'imported' | 'spark';
  /**
   * Handler that is called when the hero sub-tab selection changes.
   */
  onHeroSubTabChange?: (key: 'imported' | 'spark') => void;
  /**
   * Additional content to render alongside the carousel sub-tabs (e.g., the inline "+ Upload" button).
   */
  carouselHeaderContent?: React.ReactNode;
  /**
   * Additional content to render in the hero tab header (e.g., the inline "+ Upload" button).
   */
  heroHeaderContent?: React.ReactNode;
}

function ProductAssetsDrawerV2Trigger(props: CollapsibleDrawerTriggerProps) {
  return <CollapsibleDrawer.Trigger {...props} />;
}

function ProductAssetsDrawerV2HeroTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx('px-4', className)} id="hero" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2HeroImportedTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="imported" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2HeroSparkTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="spark" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2CarouselTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx('px-4', className)} id="carousel" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2CarouselImportedTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="imported" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerV2CarouselSparkTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="spark" {...other}>
      {children}
    </TabPanel>
  );
}

/**
 * V2 drawer content with updated layout per the V2 designs:
 * - Top-level Hero | Carousel tabs pinned to the top of the panel
 * - Imported | Spark sub-tabs rendered inline with the "+ Upload" button
 * - Updated horizontal padding (V1 used `px-10`; V2 uses `px-4` per design tokens)
 */
function ProductAssetsDrawerV2Content(props: ProductAssetsDrawerV2ContentProps) {
  const {
    children,
    className,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    carouselSubTab,
    defaultCarouselSubTab,
    onCarouselSubTabChange,
    heroSubTab,
    defaultHeroSubTab,
    onHeroSubTabChange,
    carouselHeaderContent,
    heroHeaderContent,
    ...other
  } = props;

  const heroChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2HeroTabPanel;
  });

  const carouselChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2CarouselTabPanel;
  });

  const heroChildren = React.isValidElement(heroChild)
    ? React.Children.toArray((heroChild.props as { children?: React.ReactNode }).children)
    : [];

  const heroImportedChild = heroChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2HeroImportedTabPanel;
  });

  const heroSparkChild = heroChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2HeroSparkTabPanel;
  });

  // When the hero panel is wrapped in the new Imported/Spark sub-panels, render
  // the nested tabs. Otherwise fall back to rendering the hero panel children
  // inline (the existing single-panel behavior, used when the feature flag is
  // off).
  const hasHeroSubTabs = !!(heroImportedChild || heroSparkChild);

  const carouselChildren = React.isValidElement(carouselChild)
    ? React.Children.toArray((carouselChild.props as { children?: React.ReactNode }).children)
    : [];

  const importedChild = carouselChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2CarouselImportedTabPanel;
  });

  const sparkChild = carouselChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2CarouselSparkTabPanel;
  });

  const handleCarouselSubTabChange = React.useCallback(
    (key: Key | null) => {
      if (key && (key === 'imported' || key === 'spark')) {
        onCarouselSubTabChange?.(key);
      }
    },
    [onCarouselSubTabChange],
  );

  const handleHeroSubTabChange = React.useCallback(
    (key: Key | null) => {
      if (key && (key === 'imported' || key === 'spark')) {
        onHeroSubTabChange?.(key);
      }
    },
    [onHeroSubTabChange],
  );

  return (
    <div className={cx('flex flex-col w-full', className)} {...other}>
      <Tabs
        variant={TabVariant.Underline}
        size={TabSize.Sm}
        className="w-full"
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey}
        onSelectionChange={onSelectionChange}
      >
        <div className="flex items-end pt-2">
          {/* The Underline TabList atom defaults to `justify-center w-full`,
              which centers the tabs inside the available space. Override to
              `justify-start` so Hero/Carousel hug the left edge — flush with
              the ASSETS caption above and the asset cards below. */}
          <TabList className="flex justify-start gap-4 w-auto">
            <Tab className="!px-0" id="hero" key="hero">
              Hero
            </Tab>
            <Tab className="!px-0" id="carousel" key="carousel">
              Carousel
            </Tab>
          </TabList>
        </div>
        {/* Hero tab panel — nested Imported/Spark sub-tabs when provided, otherwise single-panel */}
        <TabPanel className="m-0 mt-4" id="hero">
          {hasHeroSubTabs ? (
            <Tabs
              variant={TabVariant.Button}
              size={TabSize.Md}
              className="w-full"
              selectedKey={heroSubTab}
              defaultSelectedKey={defaultHeroSubTab}
              onSelectionChange={handleHeroSubTabChange}
            >
              <div className="flex items-center gap-2 w-full">
                <TabList className="flex">
                  <Tab id="imported" key="imported" data-testid="product-drawer-v2-hero-imported-tab">
                    Imported
                  </Tab>
                  <Tab id="spark" key="spark" data-testid="product-drawer-v2-hero-spark-tab">
                    Spark
                  </Tab>
                </TabList>
                {heroHeaderContent}
              </div>
              {heroImportedChild}
              {heroSparkChild}
            </Tabs>
          ) : (
            <>
              {heroHeaderContent && (
                <div className="flex items-center justify-end w-full mb-3">{heroHeaderContent}</div>
              )}
              {React.isValidElement(heroChild) ? (heroChild.props as { children?: React.ReactNode }).children : null}
            </>
          )}
        </TabPanel>
        {/* Carousel tab panel with nested tabs + inline upload action */}
        <TabPanel className="m-0 mt-10" id="carousel">
          <Tabs
            variant={TabVariant.Button}
            size={TabSize.Md}
            className="w-full"
            selectedKey={carouselSubTab}
            defaultSelectedKey={defaultCarouselSubTab}
            onSelectionChange={handleCarouselSubTabChange}
          >
            <div className="flex items-center gap-2 w-full">
              <TabList className="flex">
                <Tab id="imported" key="imported" data-testid="product-drawer-v2-carousel-imported-tab">
                  Imported
                </Tab>
                <Tab id="spark" key="spark" data-testid="product-drawer-v2-carousel-spark-tab">
                  Spark
                </Tab>
              </TabList>
              {carouselHeaderContent}
            </div>
            {importedChild}
            {sparkChild}
          </Tabs>
        </TabPanel>
      </Tabs>
    </div>
  );
}

/**
 * V2 drawer width override.
 *
 * The shared `CollapsibleDrawer` `contentWidth="custom"` ships fixed widths of
 * `524px` (>= xl) and `366px` (< xl). V2 holds two `w-[118px]` asset cards
 * separated by a `16px` column gap, sitting inside a `px-8` panel (32px
 * each side):
 *   `2×118 (cards) + 16 (gap-x-4) + 32 (16px margins) = 284px`
 * The grid is fixed at 252px and centered with `mx-auto`; the drawer
 * column provides enough room for 16px margins on each side.
 * The override is only applied while the drawer is open so the closed-state
 * collapse to `0px` still works.
 */
const V2_OPEN_WIDTH_OVERRIDE = 'grid-cols-[1fr_300px] max-xl:grid-cols-[1fr_300px]';

interface ProductAssetsDrawerV2DefaultHeaderProps {
  /**
   * Called when the user clicks the close button. When undefined, the close
   * button is not rendered (e.g. when the drawer is configured as `isDisabled`).
   */
  onClose?: () => void;
}

/**
 * Default V2 drawer header. Reuses the shared `Caption` atom so the styling —
 * uppercase mono `text-xs text-tertiary` with a top separator rule and a hover
 * info popup — stays in lockstep with the page section captions like
 * "YOUR HERO + CAROUSEL".
 */
function ProductAssetsDrawerV2DefaultHeader(props: ProductAssetsDrawerV2DefaultHeaderProps) {
  const { onClose } = props;

  return (
    <div className="pb-4">
      <Caption
        label="ASSETS"
        gap="gap-0"
        infoMetaData={{
          description: 'All product assets available for this PDP. Drag any asset onto the hero or carousel to use it.',
        }}
        trailingContent={
          onClose && (
            <Button
              onPress={onClose}
              aria-label="Close"
              size={ButtonSize.Sm}
              level={ButtonLevel.Ghost}
              iconStart={<XCloseIcon size={20} />}
            />
          )
        }
      >
        {/* Intentionally empty: Caption requires children */}
        <span aria-hidden="true" />
      </Caption>
    </div>
  );
}

function ProductAssetsDrawerV2(props: ProductAssetsDrawerV2Props) {
  const {
    children: childrenProp,
    contentClassName,
    headerContent,
    className,
    isOpen,
    defaultOpen,
    isDisabled,
    onOpenChange,
    ...other
  } = props;

  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child) && child.type !== ProductAssetsDrawerV2Content;
  });

  const content = React.Children.toArray(childrenProp).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerV2Content;
  });

  // Resolve the effective open state for layout purposes. Controlled callers
  // pass `isOpen`; uncontrolled callers fall back to `defaultOpen`. We don't
  // need exact state tracking here — only enough to skip the width override
  // while the drawer is closed so it can animate down to `0px`.
  const isOpenForLayout = isOpen ?? defaultOpen ?? false;

  // The shared `CollapsibleDrawer` ships a default header that uses
  // `text-lg semibold` styling. V2 wants the `Caption` look ("ASSETS" in mono
  // uppercase + separator rule). Provide a V2-flavored default; callers can
  // still override via `headerContent` (e.g. the original-view header).
  const resolvedHeaderContent = headerContent ?? (
    <ProductAssetsDrawerV2DefaultHeader onClose={isDisabled ? undefined : () => onOpenChange?.(false)} />
  );

  return (
    <CollapsibleDrawer
      title="Assets"
      side="right"
      contentWidth="custom"
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      isDisabled={isDisabled}
      onOpenChange={onOpenChange}
      className={cx(isOpenForLayout && V2_OPEN_WIDTH_OVERRIDE, className)}
      {...other}
    >
      {children}
      <CollapsibleDrawer.Content className={contentClassName} headerContent={resolvedHeaderContent}>
        {content}
      </CollapsibleDrawer.Content>
    </CollapsibleDrawer>
  );
}

export {
  ProductAssetsDrawerV2,
  ProductAssetsDrawerV2Content,
  ProductAssetsDrawerV2HeroTabPanel,
  ProductAssetsDrawerV2HeroImportedTabPanel,
  ProductAssetsDrawerV2HeroSparkTabPanel,
  ProductAssetsDrawerV2CarouselTabPanel,
  ProductAssetsDrawerV2CarouselImportedTabPanel,
  ProductAssetsDrawerV2CarouselSparkTabPanel,
  ProductAssetsDrawerV2Trigger,
};
export type { ProductAssetsDrawerV2Props, ProductAssetsDrawerV2ContentProps };
